import React from "react";
import { useEffect, useRef, useCallback } from "react";
import Overlay from "./Overlay";
import { useParams } from "react-router-dom";
import { siteMapColorHex } from "../utils/colors";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

export default function LabelMarker({
  editing,
  marker,
  onDelete,
  onMove,
  onSave,
}) {
  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const selectedTextId = useParams()?.textId;
  const editingThisLabel =
    selectedTextId && marker.id === parseInt(selectedTextId);
  const textMarkerRef = useRef(null);
  const inputRef = useRef(null);

  const finishEditing = useCallback(
    (shouldSave = false) => {
      if (shouldSave && inputRef.current) {
        if (inputRef.current.value.trim() === "") {
          // if we have no text, delete the marker
          onDelete(marker);
          return;
        }

        onSave({
          ...marker,
          text: inputRef.current.value,
        });
      }
      navigate("edit");
    },
    [marker, navigate, onDelete, onSave],
  );

  const resizeInput = useCallback(() => {
    if (inputRef.current) {
      // Create a temporary span to measure text width
      const span = document.createElement("span");
      span.style.fontSize = window.getComputedStyle(inputRef.current).fontSize;
      span.style.fontFamily = window.getComputedStyle(
        inputRef.current,
      ).fontFamily;
      span.style.visibility = "hidden";
      span.style.position = "absolute";
      span.style.whiteSpace = "nowrap";
      span.textContent = inputRef.current.value || marker.text;

      document.body.appendChild(span);
      const textWidth = span.offsetWidth;
      document.body.removeChild(span);

      // Set minimum width and add some padding
      const minWidth = 20;
      const padding = 20;
      inputRef.current.style.width = `${Math.max(minWidth, textWidth + padding)}px`;
    }
  }, [marker.text]);

  // Handle click outside to finish editing
  useEffect(() => {
    if (!editingThisLabel) return;

    const handleClickOutside = (event) => {
      const textSidebar = document.getElementById("map-route-sidebar");
      if (
        event.target.classList.contains("text-marker-input") ||
        (textSidebar && textSidebar.contains(event.target))
      ) {
        return;
      }

      if (
        !textMarkerRef.current ||
        (textMarkerRef.current && !textMarkerRef.current.contains(event.target))
      ) {
        finishEditing(true); // save on click outside, helps feel smooth
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingThisLabel, finishEditing, marker]);

  // Auto-resize input when entering edit mode
  useEffect(() => {
    if (editingThisLabel) {
      // Use setTimeout to ensure the input is fully rendered and in the DOM
      setTimeout(() => {
        if (inputRef.current) {
          // inputRef.current.focus();
          resizeInput();
        }
      }, 0);
    }
  }, [editingThisLabel, resizeInput]);

  const color = siteMapColorHex(marker.color, "black");
  const bgColor = marker.color === "white" ? "#222222" : "#ffffff";

  let fontSize;
  switch (marker.size) {
    case "md":
      fontSize = "16px";
      break;
    case "lg":
      fontSize = "18px";
      break;
    case "sm":
    default:
      fontSize = "12px";
  }

  const style = {
    color,
    fontSize,
    border: 0,
    backgroundColor: bgColor,
    opacity: 0.75,
    padding: "5px 10px",
    borderRadius: "5px",
  };
  const editingStyle = {
    ...style,
    ...{
      opacity: 1,
      minWidth: "50px",
      zIndex: 1000,
    },
  };

  const handleTab = (e) => {
    if (e.keyCode === 9) {
      finishEditing(true);
    }
  };

  const handleKeyUp = (e) => {
    e.stopPropagation(); // Prevent map from handling the event

    // Check for Enter (13) or Tab (9) using keyCode for better browser compatibility
    if (e.keyCode === 13) {
      e.preventDefault();
      finishEditing(true); // Save on Enter or Tab
    } else if (e.keyCode === 27) {
      // Escape
      e.preventDefault();
      finishEditing(false); // Don't save on Escape
    }
  };

  const handleInput = (e) => {
    e.stopPropagation();
    resizeInput();
  };

  const handleDragEnd = (e) => {
    if (!editing || !onMove) return;

    const newLat = e.latLng?.lat();
    const newLng = e.latLng?.lng();
    if (newLat && newLng) {
      const newMarker = {
        ...marker,
        lat: newLat,
        lng: newLng,
      };
      onMove(newMarker);
    }
  };

  const handleClick = () => {
    if (!editing) return;

    // our overlay sends clicks up a tad noisely (clicks inside the text-marker below trigger
    // our overlay). As such, we ignore clicks if we're already editing this label
    if (editingThisLabel) return;
    navigate(`edit/text/${marker.id}`);
  };

  return (
    <Overlay
      key={marker.id}
      position={marker}
      offset={{ x: -25, y: -15 }}
      onDragEnd={editing ? handleDragEnd : undefined}
      onClick={editing ? handleClick : undefined}
    >
      <div className="text-marker" ref={textMarkerRef}>
        {editingThisLabel ? (
          // inputs don't work on a map unless they're on a proper overlay layer (high enough up the stack
          // to receive keyboard events). We also need to prevent map events from making it past our input
          // https://developers.google.com/maps/documentation/javascript/reference/overlay-view#OverlayView.preventMapHitsFrom
          // if we find we continue needing this in different places it's probably wise moving it to the
          // Overlay component and either turning it on there or allowing controlling it
          <div
            ref={(ref) =>
              ref && google.maps.OverlayView.preventMapHitsAndGesturesFrom(ref)
            }
          >
            <input
              ref={inputRef}
              type="text"
              className="text-marker-input"
              name="text-marker-input"
              defaultValue={marker.text}
              onKeyDown={handleTab}
              onKeyUp={handleKeyUp}
              onInput={handleInput}
              style={editingStyle}
            />
          </div>
        ) : (
          <button style={style}>{marker.text}</button>
        )}
      </div>
    </Overlay>
  );
}
